/**
 * Typed hooks for the /reports (instance) API.
 *
 * A Report instance binds a template to a named run configuration —
 * default params, folder placement, audience + page overrides, delivery.
 * Cloning and deletion live here; rendering is delegated to the
 * template's render endpoint with the instance's defaults merged in by
 * the caller.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface ReportSummary {
  id: string;
  tenantId: string;
  templateId: string;
  folderId: string | null;
  name: string;
  audience: 'internal' | 'customer' | null;
  pageOrientation: 'portrait' | 'landscape' | null;
  pageSize: 'letter' | 'legal' | 'a4' | 'tabloid' | null;
  pageMargins: { top: number; right: number; bottom: number; left: number } | null;
  defaultParameters: Record<string, unknown>;
  deliveryConfig: Record<string, unknown>;
  scheduleId: string | null;
  ownerId: string | null;
  /** Hierarchy node the report belongs to (CAP3-8937). */
  scopePath: string | null;
  /** hierarchy | global | personal | restricted (CAP3-8937 / CAP3-8940). */
  visibility: 'hierarchy' | 'global' | 'personal' | 'restricted';
  createdAt: string;
  updatedAt: string;
}

/** One user and the reports grouped under them (Assigned Reports admin view). */
export interface AssignedGroup {
  user: string;
  reports: ReportSummary[];
}
/** Admin oversight: every user's assigned/owned reports within the caller's scope. */
export interface AssignedReports {
  assignedTo: AssignedGroup[];
  ownedBy: AssignedGroup[];
}

/**
 * Admin-only oversight of every user's reports at/below the caller's scope.
 * The endpoint is admin-gated server-side, so a non-admin gets 403 → we don't
 * retry and simply don't render the folder (data stays undefined). Admin-ness
 * for portal users is driven by the CUS `sage.admin` scope.
 *
 * CSAGE-353: no longer called by any UI. The "Assigned Reports" folder it fed
 * was removed from the consumer list — a saved report is private to the person
 * who saved it, and there is no product reason for one customer admin to browse
 * a colleague's copies. Managing OTHER people's subscriptions belongs in the
 * admin Subscriptions surface, which reads /schedules instead.
 *
 * Kept because the endpoint still exists and is the read side of CSAGE-198/199;
 * delete both together if that oversight model is retired.
 */
export function useAssignedReports() {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'assigned'],
    queryFn: () => api.fetch<AssignedReports>('/reports/assigned'),
    retry: false,
  });
}

/** A user's saved parameter set for a report (Tier-3 `parameters/mine`). */
export interface MyReportParams {
  reportId: string;
  params: Record<string, unknown>;
  updatedAt: string | null;
}

export function useReports(
  filters?: { templateId?: string; folderId?: string | null },
  options?: { enabled?: boolean },
) {
  const api = useApi();
  const params = new URLSearchParams();
  if (filters?.templateId) params.set('templateId', filters.templateId);
  if (filters?.folderId !== undefined) {
    params.set('folderId', filters.folderId === null ? 'null' : filters.folderId);
  }
  const qs = params.toString();
  return useQuery<ReportSummary[]>({
    queryKey: ['reports', filters?.templateId ?? null, filters?.folderId ?? 'any'],
    queryFn: () => api.fetch(`/reports${qs ? `?${qs}` : ''}`),
    enabled: options?.enabled ?? true,
  });
}

export function useReport(id: string | null) {
  const api = useApi();
  return useQuery<ReportSummary>({
    queryKey: ['reports', 'one', id],
    queryFn: () => api.fetch(`/reports/${id}`),
    enabled: !!id,
  });
}

export function useCreateReport() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      templateId: string;
      name: string;
      folderId?: string | null;
      defaultParameters?: Record<string, unknown>;
    }) =>
      api.fetch<ReportSummary>('/reports', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useUpdateReport() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<
        Pick<
          ReportSummary,
          'name' | 'folderId' | 'audience' | 'pageOrientation' | 'pageSize' | 'pageMargins'
        >
      > & { defaultParameters?: Record<string, unknown>; deliveryConfig?: Record<string, unknown> };
    }) =>
      api.fetch<ReportSummary>(`/reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['reports'] });
      void qc.invalidateQueries({ queryKey: ['reports', 'one', id] });
    },
  });
}

export function useCloneReport() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, folderId }: { id: string; name: string; folderId?: string | null }) =>
      api.fetch<ReportSummary>(`/reports/${id}/clone`, {
        method: 'POST',
        body: JSON.stringify({ name, ...(folderId !== undefined ? { folderId } : {}) }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

/**
 * CSAGE-353: save a private copy of a report the user can see — the legacy
 * module's "save a copy, then subscribe to it" flow.
 *
 * Distinct from `useCloneReport`: clone inherits the source's visibility, so a
 * clone of a shared report is another shared report in everyone's list. This
 * creates a `personal` copy assigned to the calling user, which is what makes
 * it appear in their "My Reports". `defaultParameters` carries the values
 * currently in the parameter form so the copy reruns with the user's edits.
 *
 * Invalidating ['reports'] is enough: My Reports is a client-side split of that
 * same list, and the admin oversight view's key ['reports','assigned'] is a
 * prefix match, so both refresh from the one call.
 */
export function useSaveReportAsMine() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      name,
      defaultParameters,
    }: {
      id: string;
      name?: string;
      defaultParameters?: Record<string, unknown>;
    }) =>
      api.fetch<ReportSummary>(`/reports/${id}/save-as-mine`, {
        method: 'POST',
        body: JSON.stringify({
          ...(name !== undefined ? { name } : {}),
          ...(defaultParameters !== undefined ? { defaultParameters } : {}),
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useDeleteReport() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fetch<{ ok: true }>(`/reports/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

// ─── Per-user saved parameters (Tier-3 `parameters/mine`, CAP3-8940) ────────
// Each user keeps their own saved parameter set for a report, so a shared
// report can be run with personal defaults — matching the legacy Reports
// module. Rendering merges these over the report's defaultParameters.

export function useMyReportParams(id: string | null) {
  const api = useApi();
  return useQuery<MyReportParams>({
    queryKey: ['reports', 'my-params', id],
    queryFn: () => api.fetch(`/reports/${id}/parameters/mine`),
    enabled: !!id,
  });
}

export function useSaveMyReportParams() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: Record<string, unknown> }) =>
      api.fetch<MyReportParams>(`/reports/${id}/parameters/mine`, {
        method: 'PUT',
        body: JSON.stringify(params),
      }),
    onSuccess: (_, { id }) => void qc.invalidateQueries({ queryKey: ['reports', 'my-params', id] }),
  });
}

export function useDeleteMyReportParams() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.fetch<{ ok: true }>(`/reports/${id}/parameters/mine`, { method: 'DELETE' }),
    onSuccess: (_, id) => void qc.invalidateQueries({ queryKey: ['reports', 'my-params', id] }),
  });
}
